import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/** Why the conversation ended — drives the copy + primary action. */
export type ConversationEndedReason = 'idle' | 'guardrail' | 'complete';

export interface ConversationEndedStateProps {
  /** Primary action — start a fresh conversation (idle/complete) or return home (guardrail). */
  onReset: () => void;
  /**
   * Why the conversation ended. `idle` (default) = server idle TTL; `guardrail`
   * = the hardened guardrail terminated the session (011 F3); `complete`
   * (ANA-575) = Anaka intentionally ended the call (a voice goodbye / cap). The
   * refusal or goodbye message itself is already shown in the transcript above;
   * this banner only explains that the session is closed and offers the way forward.
   */
  reason?: ConversationEndedReason;
  className?: string;
}

const COPY: Record<ConversationEndedReason, { message: string; cta: string }> = {
  idle: {
    message: 'This conversation ended from inactivity. Start a new one?',
    cta: 'Start new conversation',
  },
  guardrail: {
    message:
      'This conversation has been closed and can’t continue. Please start again from home.',
    cta: 'Start again',
  },
  complete: {
    message: 'Anaka ended the call. Start a new conversation?',
    cta: 'Start new conversation',
  },
};

export function ConversationEndedState({
  onReset,
  reason = 'idle',
  className,
}: ConversationEndedStateProps) {
  const { message, cta } = COPY[reason];
  return (
    <div
      role="region"
      aria-label="Conversation ended"
      data-testid="conversation-ended-state"
      data-reason={reason}
      className={cn(
        'flex flex-col items-start gap-3 border-t bg-muted/40 px-4 py-4 text-sm',
        className,
      )}
    >
      <p className="text-muted-foreground">{message}</p>
      <Button type="button" size="sm" onClick={onReset}>
        {cta}
      </Button>
    </div>
  );
}
